import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useQuery } from '../lib/useQuery.js';
import { useSos } from '../lib/sos.js';
import { relativeTime } from '../lib/format.js';
import { Page } from '../app/AppShell.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { Loading } from '../components/ui.js';
import type { Layout, Orientation, SosState } from '../lib/types.js';

const ORIENTATIONS: { key: Orientation; label: string }[] = [
  { key: 'landscape', label: 'Landscape screens' },
  { key: 'portrait', label: 'Portrait screens' },
];

/**
 * Emergency SOS (ADR-0029): one switch that puts every display on its SOS
 * layout over whatever its schedule says. Each display gets the layout for
 * the orientation it is seen in — a screen mounted on its side counts as the
 * other orientation — so the alert always fills the screen.
 */
export function SosScreen(): ReactNode {
  const { sos, setSos } = useSos();
  const { can } = useAuth();
  const toast = useToast();
  const canActivate = can('sos.activate');
  const canManage = can('sos.manage');
  const layouts = useQuery(() => (canManage && can('layouts.view') ? api.layouts() : Promise.resolve([])), [canManage]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<SosState>, done: string): Promise<void> {
    setBusy(true);
    try {
      setSos(await action());
      toast.push(done, 'success');
      layouts.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  if (!sos) {
    return (
      <Page title="Emergency SOS">
        <Loading what="SOS status" />
      </Page>
    );
  }

  const total = sos.displays.landscape + sos.displays.portrait;
  const missing = ORIENTATIONS.filter((o) => sos.layouts[o.key] === null);

  return (
    <Page title="Emergency SOS">
      <div className={sos.active ? 'card card-pad sos-status live' : 'card card-pad sos-status'}>
        <div className="stack">
          <strong className="sos-status-title">{sos.active ? 'SOS is live on every display' : 'SOS is off'}</strong>
          <span className="small">
            {sos.active
              ? `Activated ${relativeTime(sos.activatedAt)}${sos.activatedBy ? ` by ${sos.activatedBy.name}` : ''}. Displays show the SOS layout until you clear it.`
              : `Activating switches all ${total} display${total === 1 ? '' : 's'} to the SOS layout at once, whatever they are scheduled to show.`}
          </span>
        </div>
        {canActivate ? (
          sos.active ? (
            <button
              className="btn"
              disabled={busy}
              onClick={() => void run(() => api.clearSos(), 'SOS cleared')}
            >
              {busy ? 'Clearing…' : 'Clear SOS'}
            </button>
          ) : (
            <button className="btn sos-activate" disabled={busy} onClick={() => setConfirming(true)}>
              Activate SOS
            </button>
          )
        ) : null}
      </div>

      <div className="card card-pad stack">
        <div>
          <strong>Matches each screen's orientation</strong>
          <p className="small muted" style={{ margin: '4px 0 0' }}>
            Each display gets the SOS layout for the way its screen is seen, including a 90° mount
            rotation set in Display. If one orientation has no layout, those screens show the other
            one, letterboxed. Players keep the SOS layout downloaded in advance, so it appears
            immediately.
          </p>
        </div>

        <div className="editor halves">
          {ORIENTATIONS.map(({ key, label }) => {
            const ref = sos.layouts[key];
            const count = sos.displays[key];
            return (
              <div key={key} className="stack sos-orientation">
                <div className={`sos-frame ${key}`} aria-hidden="true">
                  <span>{ref ? ref.name : 'Built-in alert'}</span>
                </div>
                <strong>{label}</strong>
                <span className="small muted">
                  {count} display{count === 1 ? '' : 's'}
                  {ref ? ` · ${ref.width}×${ref.height}` : ''}
                  {ref && !ref.published ? ' · not published' : ''}
                </span>
                {canManage ? (
                  <label className="field">
                    SOS layout
                    <select
                      value={ref?.id ?? ''}
                      disabled={busy}
                      onChange={(e) =>
                        void run(
                          () => api.updateSos({ [`${key}LayoutId`]: e.target.value || null }),
                          'SOS layout updated',
                        )
                      }
                    >
                      <option value="">None (built-in alert on activation)</option>
                      {sortByFit(layouts.data ?? [], key).map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name} ({l.width}×{l.height}){l.status !== 'published' ? ' (draft)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {ref && can('layouts.edit') ? (
                  <Link className="btn small" to={`/layouts/${ref.id}`}>
                    Edit layout
                  </Link>
                ) : null}
              </div>
            );
          })}
        </div>

        {canManage && missing.length > 0 ? (
          <div className="row wrap">
            <button
              className="btn"
              disabled={busy}
              onClick={() => void run(() => api.createSosLayouts(), 'SOS layouts created')}
            >
              Create built-in SOS layout{missing.length > 1 ? 's' : ''}
            </button>
            <span className="small muted">
              A red full-screen alert sized for{' '}
              {missing.map((m) => m.key).join(' and ')} screens, ready to edit.
            </span>
          </div>
        ) : null}
      </div>

      {confirming ? (
        <Modal title="Activate SOS?" onClose={() => setConfirming(false)}>
          <div className="stack">
            <p style={{ margin: 0 }}>
              All {total} display{total === 1 ? '' : 's'} will switch to the SOS layout now —{' '}
              {sos.displays.landscape} landscape, {sos.displays.portrait} portrait — and stay on it
              until SOS is cleared.
            </p>
            <div className="row wrap">
              <button
                className="btn sos-activate"
                disabled={busy}
                onClick={() => void run(() => api.activateSos(), 'SOS activated')}
              >
                {busy ? 'Activating…' : 'Activate SOS'}
              </button>
              <button className="btn" disabled={busy} onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </Page>
  );
}

/** Layouts shaped for this orientation first, so the right one is easy to find. */
function sortByFit(layouts: Layout[], orientation: Orientation): Layout[] {
  const fits = (l: Layout): boolean => (orientation === 'portrait' ? l.height > l.width : l.width >= l.height);
  return [...layouts].sort((a, b) => Number(fits(b)) - Number(fits(a)) || a.name.localeCompare(b.name));
}
